import c from '../constants/Constants';

/**
 * Interceptor global de fetch para las llamadas a la API.
 *
 * La app no tiene un cliente HTTP central (cada archivo de src/api arma su
 * propio fetch), asi que el punto unico de control es envolver window.fetch.
 *
 * Reglas:
 *  - 401: se corta la sesion una sola vez (limpieza + redirect a /login) y
 *    desde ese momento toda llamada a la API se responde localmente con un 401
 *    sintetico, sin salir a la red. Nunca se reintenta un 401.
 *  - 5xx / error de red: reintento con backoff exponencial (1s, 2s) y tope de
 *    MAX_ATTEMPTS. Solo para metodos idempotentes (GET/HEAD).
 *  - Las llamadas a /auth/ quedan siempre fuera del corte: el login tiene que
 *    poder ejecutarse con la sesion vencida, y un 401 ahi significa
 *    "credenciales invalidas", no "token vencido".
 */

const MAX_ATTEMPTS = 3; //1 intento + 2 reintentos
const BASE_DELAY = 1000; //1s, 2s
const RETRIABLE_METHODS = ['GET', 'HEAD'];

//Latch: una vez que la API contesta 401 dejamos de pegarle hasta un login nuevo
let sessionExpired = false;
let onSessionExpired = null;

function getUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input.url === 'string') return input.url;
  return '';
}

function getMethod(input, init) {
  if (init && init.method) return init.method.toUpperCase();
  if (input && typeof input.method === 'string')
    return input.method.toUpperCase();
  return 'GET';
}

function isApiRequest(url) {
  return !!c.BASE_URL && url.indexOf(c.BASE_URL) === 0;
}

function isAuthRequest(url) {
  return url.indexOf(c.BASE_URL + '/auth/') === 0;
}

function unauthenticatedResponse() {
  return new Response(JSON.stringify({ message: 'Unauthenticated.' }), {
    status: 401,
    statusText: 'Unauthorized',
    headers: { 'Content-Type': 'application/json' }
  });
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Marca la sesion como vencida. Idempotente: el handler (logout + redirect)
 * corre una sola vez por mas que lleguen varios 401 en paralelo.
 */
function expireSession() {
  if (sessionExpired) return;
  sessionExpired = true;
  if (onSessionExpired) onSessionExpired();
}

export function resetSessionExpired() {
  sessionExpired = false;
}

export function isSessionExpired() {
  return sessionExpired;
}

/**
 * @param {Function} handler callback que corta la sesion (limpieza + redirect)
 */
export function installHttpInterceptor(handler) {
  onSessionExpired = handler;

  if (typeof window === 'undefined' || window.__httpInterceptorInstalled)
    return;
  window.__httpInterceptorInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    const url = getUrl(input);

    //Todo lo que no sea nuestra API pasa derecho
    if (!isApiRequest(url)) return nativeFetch(input, init);

    const auth = isAuthRequest(url);

    //Sesion vencida: cortamos sin tocar la red
    if (sessionExpired && !auth) {
      return Promise.resolve(unauthenticatedResponse());
    }

    const method = getMethod(input, init);
    const retriable = RETRIABLE_METHODS.indexOf(method) !== -1;

    const attempt = n => {
      return nativeFetch(input, init).then(
        response => {
          if (response.status === 401 && !auth) {
            //Nunca se reintenta un 401: token invalido o vencido
            expireSession();
            return response;
          }

          if (auth && url === c.BASE_URL + '/auth/login' && response.ok) {
            //Login nuevo: se habilita otra vez el trafico a la API
            resetSessionExpired();
          }

          if (response.status >= 500 && retriable && n < MAX_ATTEMPTS) {
            return wait(BASE_DELAY * Math.pow(2, n - 1)).then(() =>
              attempt(n + 1)
            );
          }

          return response;
        },
        error => {
          //Error de red (sin respuesta): mismo backoff acotado
          if (retriable && n < MAX_ATTEMPTS) {
            return wait(BASE_DELAY * Math.pow(2, n - 1)).then(() =>
              attempt(n + 1)
            );
          }
          return Promise.reject(error);
        }
      );
    };

    return attempt(1);
  };
}

export default {
  installHttpInterceptor,
  resetSessionExpired,
  isSessionExpired
};
